import React, { useMemo, useState } from 'react'
import Icon from '../Icon2/Icon'
import Avatar from '../Avatar/Avatar'
import DateLabel from '../DateLabel/DateLabel'
import Tooltip from '../Tooltip/Tooltip'
import Pill from '../Pills/Pill'
import './DraftVersionTimeline.scss'
import SkeletonTile from '../../components/SkeletonTile/SkeletonTile'

interface Draft {
  uid: string
  modified_by: string
  _minor_version_no: string
  created_at: string
  updated_at: string
  entry_uid: string
  local: string
  changes: any
  draft_deleted?: boolean
}

interface IDraftVersionTimeline {
  drafts: Draft[]
  hasMore: boolean
  onLoadMore: () => void | Promise<void>
  onCompare?: (draft: Draft) => void
  loading?: boolean
  loadingMore?: boolean
  version: string
  versionName?: string
  testId?: string
  loader?: boolean
  skeletonLoaderCount?: number
  totalDraftCount: number
}



export const getRandomColor = (initials?: any): any => {
  const colorList: any = ['#FED6D0', '#FCA885', '#FFE666', '#97D9FF', '#67C1B0', '#D79FF1']

  const charCode = initials && initials.charCodeAt(0)
  switch (true) {
    case charCode > 64 && charCode < 68:
      return '#FED6D0'
    case charCode > 67 && charCode < 71:
      return '#FCA885'
    case charCode > 70 && charCode < 74:
      return '#FFE666'
    case charCode > 73 && charCode < 77:
      return '#97D9FF'
    case charCode > 76 && charCode < 80:
      return '#67C1B0'
    case charCode > 79 && charCode < 83:
      return '#D79FF1'
    case charCode > 82 && charCode < 86:
      return '#FED6D0'
    case charCode > 85 && charCode < 89:
      return '#FCA885'
    case charCode > 88 && charCode < 92:
      return '#FFE666'
    default:
      return colorList[Math.floor(Math.random() * colorList.length)]
  }
}


const DraftHeader: React.FC<{ version: string; versionName?: string }> = ({ version, versionName }) => {
  return (
    <div className="DraftVersionTimeline__header">
      <div className="DraftVersionTimeline__header-icon">
        <Icon icon="VersionBadgeLatest" version="v2" size="mini" />
      </div>
      <div className="DraftVersionTimeline__header-title">
        {versionName ? versionName : `Version ${version}`}
        <Pill 
          shouldHaveBorder={false} 
          isSortable={false} 
          size="small"
          variant="label" 
          status="default" 
          items={[{ id: 1, text: 'Draft' }]} 
          className="ml-5"
          stroke={'#007A52'}
          background={"#F5FFFC"}
          testId="cs-draft-version-timeline-draft"
        />
      </div>
    </div>
  )
}

const DraftEntry: React.FC<{
  draft: Draft
  onCompare?: (draft: Draft) => void
  draftPositionIndex: number
  savedVersion: string
  savedVersionName?: string
}> = ({ draft, onCompare, draftPositionIndex, savedVersion, savedVersionName }) => {
  const [showActionButtons, setShowActionButtons] = useState(false)

  const handleCompareClick = (e: React.MouseEvent) => {
    if (onCompare) {
      onCompare(draft)
    }
  }

  const showCompareButton = onCompare && !draft.draft_deleted
  const compareTooltipContent = `Compare draft ${draftPositionIndex} with ${savedVersionName ? savedVersionName : `Version ${savedVersion}`}`

  return (
    <div 
      className="DraftVersionTimeline__entry"
      onMouseEnter={() => setShowActionButtons(true)}
      onMouseLeave={() => setShowActionButtons(false)}
      onFocus={() => setShowActionButtons(true)}
      onBlur={() => setShowActionButtons(false)}
    >
      <div className="DraftVersionTimeline__entry-content">
        <div className="DraftVersionTimeline__timestamp">
        <div className="DraftVersionTimeline__timestamp-content">
        <Icon icon="Clock" version="v2" size="small" />
        <DateLabel date={draft.updated_at} type="medium" />
        </div>
          {draft.draft_deleted && (
            <Pill 
              shouldHaveBorder={false} 
              isSortable={false} 
              size="small"
              variant="label" 
              items={[{ id: 1, text: 'Deleted' }]} 
              className="DraftVersionTimeline__deleted-pill"
              testId="cs-draft-version-timeline-deleted"
            />
          )}
        </div>
        <div className="DraftVersionTimeline__user-info">
          {(() => {
          
            return draft.modified_by ? (
              <div className="DraftVersionTimeline__user-avatar-wrapper">
                <Avatar 
                  avatars={[{
                    id: 1,
                    name: draft.modified_by,
                    image: '',
                    email: '',
                    avatarColor: '#6E6B86'
                  }]}
                  type="text"
                  size="x-small"
                  showStatus={false}
                  testId="cs-draft-version-timeline-user-avatar"
                />
              </div>
            ) : null
          })()}
          <div className="DraftVersionTimeline__user-name">
            {(() => {
              return draft.modified_by ? `By ${draft.modified_by}` : ''
            })()}
          </div>
        </div>
        {showCompareButton && (
          <div className={`DraftVersionTimeline__action-buttons ${showActionButtons ? 'DraftVersionTimeline__action-buttons--show' : ''}`}>
            <Tooltip content={compareTooltipContent} position="bottom" showArrow={false}>
              <button
                type="button"
                data-test-id="cs-draft-version-timeline-compare"
                onClick={handleCompareClick}
                aria-label={compareTooltipContent}
                className="DraftVersionTimeline__action-button"
              >
                <Icon icon="Compare" version='v2' size="small" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  )
}

const DraftVersionTimelineSkeleton: React.FC = () => {
  return (
    <div className="DraftVersionTimeline__entry">
      <div className="DraftVersionTimeline__entry-content">
        <div className="DraftVersionTimeline__timestamp">
          <SkeletonTile
            numberOfTiles={1}
            tileHeight={12}
            tileWidth={16}
            tileRadius={4}
            tileBottomSpace={0}
            tileTopSpace={0}
            tileleftSpace={0}
          />
          <SkeletonTile
            numberOfTiles={1}
            tileHeight={12}
            tileWidth={120}
            tileRadius={4}
            tileBottomSpace={8}
            tileTopSpace={0}
            tileleftSpace={8}
          />
        </div>
        <div className="DraftVersionTimeline__user-info">
          <SkeletonTile
            numberOfTiles={1}
            tileHeight={16}
            tileWidth={16}
            tileRadius={50}
            tileBottomSpace={0}
            tileTopSpace={0}
            tileleftSpace={0}
          />
          <SkeletonTile
            numberOfTiles={1}
            tileHeight={12}
            tileWidth={100}
            tileRadius={4}
            tileBottomSpace={16}
            tileTopSpace={0}
            tileleftSpace={8}
          />
        </div>
      </div>
    </div>
  )
}

const DraftVersionTimeline: React.FunctionComponent<IDraftVersionTimeline> = ({
  drafts,
  hasMore,
  onLoadMore,
  onCompare,
  loading = false,
  loadingMore = false,
  testId = 'cs-draft-version-timeline',
  version,
  versionName,
  loader = true,
  skeletonLoaderCount = 1,
  totalDraftCount
}) => {
  if (!drafts || !Array.isArray(drafts)) {
    return null;
  }

  if (drafts.length === 0 && (!loading || !loader)) {
    return null
  }

  const validDrafts = useMemo(() => drafts.filter((draft) => draft?.uid), [drafts])
  const maxMinorVersion = totalDraftCount || validDrafts.length

  return (
    <div className="DraftVersionTimeline" data-test-id={testId}>
      <div className="DraftVersionTimeline__container">
        {validDrafts.length > 0 && version && <DraftHeader version={version} versionName={versionName} />}

        {loading && loader && validDrafts.length === 0 ? (
          <>
            {Array.from({ length: Math.max(1, skeletonLoaderCount) }, (_, index) => (
              <DraftVersionTimelineSkeleton key={`skeleton-${index}`} />
            ))}
          </>
        ) : (
          <div className="DraftVersionTimeline__entries">
            {validDrafts.map((draft, arrayIndex) => {
              const draftPositionIndex = Math.max(maxMinorVersion - arrayIndex, 1)
              return (
                <DraftEntry 
                  key={draft.uid || `draft-${arrayIndex}`} 
                  draft={draft} 
                  onCompare={onCompare} 
                  draftPositionIndex={draftPositionIndex}
                  savedVersion={version}
                  savedVersionName={versionName}
                />
              )
            })}

            {loadingMore && (
              <>
                {Array.from({ length: Math.min(Math.max(1, skeletonLoaderCount), 3) }, (_, index) => (
                  <DraftVersionTimelineSkeleton key={`loading-${index}`} />
                ))}
              </>
            )}

            {hasMore && !loadingMore && onLoadMore && (
              <button
                type="button"
                className="DraftVersionTimeline__load-more"
                onClick={onLoadMore}
              >
                <div className="DraftVersionTimeline__dots">...</div>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DraftVersionTimeline
